<template>
    <ModalBase
        name="addSancion"
        :title="'Nueva sanción para ' + deportistaName"
        @close="close"
    >
        <ValidationObserver tag="form" ref="observer" class="tw:flex tw:flex-col tw:gap-4" @submit.prevent="submit">
            <div class="tw:grid tw:grid-cols-2 tw:gap-4">
                <BaseDateInput label="Fecha" name="fecha" id="fecha" rules="required" :value="fecha" @input="fecha = $event" />
                <BaseDateInput label="Sin jugar hasta" name="sin_jugar_hasta" id="sin_jugar_hasta" rules="required" :value="sin_jugar_hasta" @input="sin_jugar_hasta = $event" />
            </div>

            <BaseInputText label="Motivo" name="motivo" rules="required" :value="motivo" @input="motivo = $event" />
            <BaseInputText label="Descripción" name="descripcion" rules="required" :value="descripcion" @input="descripcion = $event" />
            <BaseInputText label="Sanción" name="sancion" rules="required" :value="sancion" @input="sancion = $event" />

            <BaseInputNumber label="Importe" name="importe" rules="required" :value="importe" @input="importe = $event" />

            <BaseMultiselect
                label="Sanción impuesta por"
                name="impuesta"
                rules="required"
                :value="impuesta"
                @input="impuesta = $event"
                :options="sancionadores"
                value-key="id"
                label-key="nombre"
            />

            <BaseMultiselect
                label="Tiene que pagar"
                name="pagar"
                rules="required"
                :value="pagar"
                @input="pagar = $event"
                :options="deudores"
                value-key="id"
                label-key="nombre"
            />

            <BaseMultiselect
                label="Sanción pagada"
                name="pagado"
                rules="required"
                :value="pagado"
                @input="pagado = $event"
                :options="pagadaOptions"
                value-key="id"
                label-key="nombre"
            />

            <Button class="tw:justify-center" type="submit" :disabled="loading">Sancionar</Button>
        </ValidationObserver>
    </ModalBase>
</template>

<script setup>
    import { ref, computed, getCurrentInstance } from 'vue'
    import ModalBase from '@/modules/admin/ui/ModalBase.vue'
    import BaseDateInput from '@/modules/admin/ui/BaseDateInput.vue'
    import BaseInputText from '@/modules/admin/ui/BaseInputText.vue'
    import BaseInputNumber from '@/modules/admin/ui/BaseInputNumber.vue'
    import BaseMultiselect from '@/modules/admin/ui/BaseMultiselect.vue'
    import Button from '@/modules/admin/ui/Button.vue'

    const props = defineProps({
        deportista: {
            type: Object,
            required: true
        },
        onSubmit: {
            type: Function,
            required: true
        }
    })

    const emit = defineEmits(['close'])
    const instance = getCurrentInstance()

    const deportistaName = computed(() => {
        const name = props.deportista.name || props.deportista.nombre || ''
        const surnames = props.deportista.surnames || props.deportista.apellidos || ''
        return [name, surnames].filter(Boolean).join(' ')
    })

    // ==================== FORM FIELDS ====================
    const fecha = ref(null)
    const sin_jugar_hasta = ref(null)
    const motivo = ref('')
    const descripcion = ref('')
    const sancion = ref('')
    const importe = ref('')
    const impuesta = ref(null)
    const pagar = ref(null)
    const pagado = ref(null)
    const loading = ref(false)
    const observer = ref(null)

    // ==================== STATIC OPTIONS ====================
    const sancionadores = [
        { id: 0, nombre: 'Federación' },
        { id: 1, nombre: 'Club' },
        { id: 2, nombre: 'Cuerpo técnico' }
    ]

    const deudores = [
        { id: 0, nombre: 'Club' },
        { id: 1, nombre: 'Deportista' }
    ]

    const pagadaOptions = [
        { id: 1, nombre: 'Pagado' },
        { id: 0, nombre: 'No pagado' }
    ]

    // ==================== HELPERS ====================
    /**
     * Convierte YYYY-MM-DD (formato <input type="date">) a m-d-Y (formato API)
     */
    const toApiDateFormat = isoDate => {
        if (!isoDate) return null
        const [y, m, d] = isoDate.split('-')
        return `${m}-${d}-${y}`
    }

    // ==================== METHODS ====================

    /**
     * Resetea todos los campos del formulario a sus valores iniciales
     */
    const resetForm = () => {
        fecha.value = null
        sin_jugar_hasta.value = null
        motivo.value = ''
        descripcion.value = ''
        sancion.value = ''
        importe.value = ''
        impuesta.value = null
        pagar.value = null
        pagado.value = null
        observer.value?.reset()
    }

    const close = () => {
        resetForm()
        instance.proxy.$modal.hide('addSancion')
        emit('close')
    }

    const submit = async() => {
        const isValid = await observer.value.validate()
        if (isValid) {
            loading.value = true

            const formData = {
                fecha: toApiDateFormat(fecha.value),
                sin_jugar_hasta: toApiDateFormat(sin_jugar_hasta.value),
                motivo: motivo.value,
                descripcion: descripcion.value,
                sancion: sancion.value,
                importe: importe.value,
                impuesta: impuesta.value,
                pagar: pagar.value,
                pagado: pagado.value
            }

            try {
                await props.onSubmit(formData)
                close()
            } catch {
                // Error handled by composable via toast
            } finally {
                loading.value = false
            }
        }
    }
</script>
